import { Route, Outlet } from "react-router-dom";
import { ActionsProvider } from "src/context/Actions/provider";
import { URLHandler } from "src/services/URLHandler";
import { Loading } from "src/components/utils/Loading";
import { useActionPlanRouter } from "src/components/Actions/ActionPlanRouter";
import { useGetCompanyPosts } from "src/api/client-api/post/useGetAllPosts";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { useEffect, useState } from "react";
import { useCompanyMetadata } from "src/context/Company/Metadata/useCompanyMetadata";
import { useCompanyScores } from "src/context/Company/Scores/useCompanyScores";

export const useTrajectoryCompanyRouter = () => {
    const actionPlanRouter = useActionPlanRouter();
    return <Route element={<ActionCompanyContext />}>{actionPlanRouter}</Route>;
};

const ActionCompanyContext = () => {
    const { selectedCompany } = useFoodpilotContext();
    const { company } = useCompanyMetadata();
    const { scoresTree, companyScores, scoreState, ssq } = useCompanyScores();

    const [companyId, setCompanyId] = useState<number | null>(null);

    useEffect(() => {
        if (selectedCompany === null) return;
        setCompanyId(selectedCompany.id);
    }, [selectedCompany]);

    // const isReady = (isRefetching: boolean, isFetched: boolean): boolean => {
    //     if (isRefetching === true) {
    //         return false;
    //     }
    //     return !isFetched;
    // };

    const {
        posts,
        isFetched: areCompanyPostsFetched,
        isRefetching: areCompanyPostsRefetching,
        isLoading: areCompanyPostsLoading,
    } = useGetCompanyPosts({
        entityId: company.id,
        status: "calculation",
    });
    const elligiblePostsForActionPlan = posts?.flatMap((post) => {
        const postCampaign = post.campaign;
        if (postCampaign === undefined) return [];

        // An Elligible Post can only be LOCKED;
        if (post.isLocked === false) return [];

        return [
            {
                id: post.id,
                isLocked: post.isLocked,
                campaign: {
                    id: postCampaign.id,
                    label: postCampaign.label,
                    startDate: postCampaign.startDate,
                    endDate: postCampaign.endDate,
                },
                scores: post.scores,
            },
        ];
    });

    const areCompanyPostsReady =
        areCompanyPostsLoading === false && areCompanyPostsFetched && areCompanyPostsRefetching === false;

    const areAllReady = [ssq.isSsqReady, ssq.isPostReady, areCompanyPostsReady].every((value) => value === true);

    if (areAllReady === false) {
        return <Loading />;
    }

    if (ssq.ssq === undefined || ssq.post === undefined) {
        return <>You must answer a questionnaire before entering Trajectory</>;
    }

    if (scoresTree.scores === undefined) {
        return <>No Scores !</>;
    }

    if (companyId === null) return;
    return (
        <ActionsProvider
            elligibleStartingPosts={elligiblePostsForActionPlan}
            ssq={ssq.ssq}
            scoresEnabled={companyScores}
            extraFilterForAction={{
                "startSsqPost.company.id": companyId,
            }}
            scoreState={scoreState.state}
            urls={{
                noActionPlan: () => URLHandler.company.actions.noActionPlan(company.id),
                planning: () => URLHandler.company.actions.planning(company.id),
                planningList: () => URLHandler.company.actions.planningList(company.id),
                planningPreview: () => URLHandler.company.actions.planningPreview(company.id),
                trajectory: () => URLHandler.company.actions.trajectory(company.id),
                trajectoryBarChart: () => URLHandler.company.actions.trajectoryBarChart(company.id),
                trajectoryCards: () => URLHandler.company.actions.trajectoryCards(company.id),
                editPlanning: (actionId: string) => URLHandler.company.actions.editPlanning(company.id, actionId),
                chart: (scoreId: number) => URLHandler.company.actions.chart(company.id, scoreId),
            }}
        >
            <Outlet />
        </ActionsProvider>
    );
};
